/**
 * AvailabilityRenderer — bookable time-slot picker. Available slots are
 * tappable and on tap fire onSendMessage with structured_value=slot.id.
 * Disabled slots show as muted with the unavailable_reason as a title
 * tooltip.
 *
 * Slots arrive ordered and stack vertically — full-width pill rows so a
 * 380px embed bubble shows every option at a glance. Horizontal scroll
 * was tried first but hid the choices behind a swipe and pushed the
 * scroll-content past the bubble's intrinsic width.
 *
 * History: shows the chosen slot prominently, others muted.
 */
import React from 'react';
import type { AvailabilityContentItem, ChatAvailabilitySlot } from '@wg/types';
import type { Renderer } from '../registry';

function formatPrice(p: ChatAvailabilitySlot['price']): string | null {
  if (!p) return null;
  if (p.display) return p.display;
  if (typeof p.amount === 'number' && p.currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: p.currency,
        maximumFractionDigits: 0,
      }).format(p.amount);
    } catch {
      return `${p.amount} ${p.currency}`;
    }
  }
  return null;
}

const AvailabilityRenderer: Renderer = ({ item, ctx }) => {
  const it = item as AvailabilityContentItem;
  const inert = ctx.isHistory;
  const chosenId =
    typeof ctx.answeredWith === 'string' ? ctx.answeredWith : null;
  if (!it.slots || it.slots.length === 0) return null;

  return (
    <div className="wg-chat__avail" data-inert={inert ? 'true' : undefined}>
      {it.title && <p className="wg-chat__chips-q">{it.title}</p>}
      <div
        className="wg-chat__avail-row"
        role="list"
        aria-label={it.title || 'Available times'}
      >
        {it.slots.map(slot => {
          const isChosen = chosenId === slot.id;
          const disabled = !slot.available || (inert && !isChosen);
          const price = formatPrice(slot.price);
          const cls = [
            'wg-chat__avail-slot',
            isChosen ? 'is-chosen' : null,
            !slot.available ? 'is-unavailable' : null,
            disabled && !isChosen ? 'is-muted' : null,
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              key={slot.id}
              type="button"
              role="listitem"
              className={cls}
              disabled={disabled}
              title={slot.unavailable_reason || undefined}
              onClick={() => {
                if (disabled || inert) return;
                ctx.onSendMessage(slot.label, {
                  in_response_to: it.prompt_id,
                  structured_value: slot.id,
                });
              }}
            >
              <span className="wg-chat__avail-label">{slot.label}</span>
              {price && <span className="wg-chat__avail-price">{price}</span>}
              {typeof slot.remaining === 'number' &&
                slot.remaining > 0 &&
                slot.remaining <= 3 && (
                  <span className="wg-chat__avail-remaining">
                    {slot.remaining} left
                  </span>
                )}
              {!slot.available && slot.unavailable_reason && (
                <span className="wg-chat__avail-reason">
                  {slot.unavailable_reason}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AvailabilityRenderer;
